/**
 * ClinicsToolbar — search, tag filter chips, "no usage" chip, plan select,
 * sort dropdown (design §9.1 pattern), results count, tag manager button,
 * print button (filtered list → landscape PDF via nivPrintListPdf).
 */

import { useRef, useState } from "react";
import {
  ArrowsDownUp,
  CaretDown,
  Check,
  Funnel,
  MagnifyingGlass,
  Printer,
  Tag,
} from "@phosphor-icons/react";
import useOutsideClose from "../shared/hooks/useOutsideClose";
import { UserTagDot } from "../shared/ClinicUserTags";

export const PLAN_OPTIONS = [
  { value: "all", label: "כל המסלולים" },
  { value: "trial", label: "ניסיון" },
  { value: "starter", label: "סטארטר" },
  { value: "starter-plus", label: "סטארטר פלוס" },
  { value: "premium", label: "פרימיום" },
  { value: "custom1", label: "פיילוט - חינם" },
];

export const SORT_OPTIONS = [
  { value: "createdAt", label: "תאריך הרשמה" },
  { value: "name", label: "שם" },
  { value: "patients", label: "מספר מטופלים" },
  { value: "amount", label: "סכום תשלום" },
  { value: "nextPayment", label: "תשלום הבא" },
  { value: "revenue", label: 'סה"כ הכנסות' },
  { value: "lastContactAt", label: "קשר אחרון" },
  { value: "lastLoginAt", label: "כניסה אחרונה" },
];

function SortDropdown({ sortField, sortDir, onChange }) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  useOutsideClose(ref, () => setOpen(false), open);
  const current = SORT_OPTIONS.find((o) => o.value === sortField) || SORT_OPTIONS[0];
  return (
    <div className="nivck2-sort" ref={ref}>
      <button
        type="button"
        className={`nivck2-sort-trigger${open ? " is-open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <ArrowsDownUp size={14} />
        <span className="nivck2-sort-label">{current.label}</span>
        <span className="nivck2-sort-dir">{sortDir === "asc" ? "↑" : "↓"}</span>
        <CaretDown size={12} className="nivck2-sort-caret" />
      </button>
      {open && (
        <div className="nivck2-sort-menu" role="listbox">
          {SORT_OPTIONS.map((opt) => {
            const active = opt.value === sortField;
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={active}
                className={`nivck2-sort-option${active ? " active" : ""}`}
                onClick={() => {
                  onChange(opt.value);
                  if (active) return;
                  setOpen(false);
                }}
              >
                <span className="nivck2-sort-option-label">{opt.label}</span>
                {active && (
                  <span className="nivck2-sort-option-dir">
                    {sortDir === "asc" ? "עולה" : "יורד"}
                  </span>
                )}
                {active && <Check size={13} weight="bold" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ClinicsToolbar({
  searchTerm,
  onSearch,
  tags,
  selectedTagIds,
  onToggleTag,
  onClearTags,
  noUsageOnly,
  onToggleNoUsage,
  planFilter,
  onPlanFilter,
  sortField,
  sortDir,
  onSortChange,
  resultCount,
  onManageTags,
  onPrint,
  isPrinting = false,
}) {
  const noTagFilter = selectedTagIds.length === 0 && !noUsageOnly;
  return (
    <div className="nivck2-toolbar">
      <div className="nivck2-toolbar-row">
        <div className="nivck2-search">
          <MagnifyingGlass size={16} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="חיפוש לפי שם קליניקה, בעלים או טלפון…"
            aria-label="חיפוש קליניקות"
          />
        </div>
        <div className="nivck2-select">
          <Funnel size={14} />
          <select value={planFilter} onChange={(e) => onPlanFilter(e.target.value)} aria-label="סינון לפי מסלול">
            {PLAN_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <SortDropdown sortField={sortField} sortDir={sortDir} onChange={onSortChange} />
        <span className="nivck2-count">{resultCount} קליניקות</span>
        <button type="button" className="nivck2-btn-secondary nivck2-manage-tags" onClick={onManageTags}>
          <Tag size={14} weight="fill" />
          ניהול תגיות
        </button>
        <button
          type="button"
          className="nivck2-btn-secondary"
          onClick={onPrint}
          disabled={isPrinting || !resultCount}
          title="הדפסת הרשימה המסוננת כ-PDF"
        >
          <Printer size={14} weight="fill" />
          {isPrinting ? "מכין…" : "הדפסה"}
        </button>
      </div>

      <div className="nivck2-chips" role="group" aria-label="סינון לפי תגיות">
        <button
          type="button"
          className={`nivck2-chip nivck2-chip-all${noTagFilter ? " active" : ""}`}
          onClick={onClearTags}
        >
          הכל
        </button>
        {tags.map((tag) => {
          const active = selectedTagIds.includes(tag.id);
          return (
            <button
              key={tag.id}
              type="button"
              className={`nivck2-chip nivck2-chip-tag nivut-c-${tag.color}${active ? " active" : ""}`}
              onClick={() => onToggleTag(tag.id)}
              aria-pressed={active}
            >
              <UserTagDot color={tag.color} size={7} />
              {tag.label}
            </button>
          );
        })}
        <button
          type="button"
          className={`nivck2-chip nivck2-chip-nousage${noUsageOnly ? " active" : ""}`}
          onClick={onToggleNoUsage}
          aria-pressed={noUsageOnly}
        >
          ללא שימוש
        </button>
      </div>
    </div>
  );
}

export default ClinicsToolbar;
